import { RefObject, useEffect, useState } from 'react';
import { generatePath } from 'react-router-dom';
import { PermissionEnum } from '@ui/src';
import { CorporateGeneralInfo } from '../../types';

import ROUTES from '../../../../routes';

const useGetOrganizationTabs = ({
  customerId,
  cardsEnabled,
  activePermissions,
  baseInformation,
  tabsWrapperRef,
  tabsRef,
  isLoading,
}: {
  activePermissions?: PermissionEnum[];
  customerId?: string;
  cardsEnabled?: boolean;
  baseInformation?: Partial<CorporateGeneralInfo>;
  tabsWrapperRef: RefObject<HTMLDivElement | null>;
  tabsRef: RefObject<HTMLDivElement | null>;
  isLoading?: boolean;
}) => {
  const [withOverflow, setWithOverflow] = useState(false);
  const [hiddenRoutes, setHiddenRoutes] = useState<
    { name: string; path: string; hidden?: boolean }[]
  >([]);
  const [tabs, setTabs] = useState<
    { name: string; path: string; hidden?: boolean }[]
  >([
    {
      name: 'Overview',
      path: generatePath(ROUTES.CUSTOMERS_CORPORATE_OVERVIEW, {
        customerId: customerId as string,
      }),
    },
    {
      name: 'Accounts',
      path: generatePath(ROUTES.CUSTOMERS_CORPORATE_ACCOUNTS, {
        customerId: customerId as string,
      }),
    },
    {
      name: 'External Integration',
      path: generatePath(ROUTES.CUSTOMERS_CORPORATE_EXTERNAL_ACCOUNTS, {
        customerId: customerId as string,
      }),
      hidden: !activePermissions?.includes(
        PermissionEnum.PSP_INTEGRATION_ACCOUNT_LIST,
      ),
    },
    {
      name: 'Cards',
      path: generatePath(ROUTES.CUSTOMERS_CORPORATE_CARDS, {
        customerId: customerId as string,
      }),
      hidden: !cardsEnabled,
    },
    {
      name: 'AML & KYC',
      path: generatePath(ROUTES.CUSTOMERS_CORPORATE_AML, {
        customerId: customerId as string,
      }),
    },
    {
      name: 'Tariff',
      path: generatePath(ROUTES.CUSTOMERS_CORPORATE_TARIFFS, {
        customerId: customerId as string,
      }),
    },
    {
      name: 'Documents',
      path: generatePath(ROUTES.CUSTOMERS_CORPORATE_DOCUMENTS, {
        customerId: customerId as string,
      }),
      hidden: !activePermissions?.includes(
        PermissionEnum.CORPORATE_CUSTOMERS_DOCUMENTS_LIST,
      ),
    },
    {
      name: 'Statistics',
      path: generatePath(ROUTES.CUSTOMERS_CORPORATE_STATISTICS, {
        customerId: customerId as string,
      }),
    },
    {
      name: 'Beneficiaries',
      path: generatePath(ROUTES.CUSTOMERS_CORPORATE_BENEFICIARIES, {
        customerId: customerId as string,
      }),
      hidden: !activePermissions?.includes(PermissionEnum.BENEFICIARIES_LIST),
    },
  ]);

  const defaultTabs = [
    {
      name: 'Overview',
      path: generatePath(ROUTES.CUSTOMERS_CORPORATE_OVERVIEW, {
        customerId: customerId as string,
      }),
    },
    {
      name: 'Accounts',
      path: generatePath(ROUTES.CUSTOMERS_CORPORATE_ACCOUNTS, {
        customerId: customerId as string,
      }),
    },
    {
      name: 'External Integration',
      path: generatePath(ROUTES.CUSTOMERS_CORPORATE_EXTERNAL_ACCOUNTS, {
        customerId: customerId as string,
      }),
      hidden: !activePermissions?.includes(
        PermissionEnum.PSP_INTEGRATION_ACCOUNT_LIST,
      ),
    },
    {
      name: 'Cards',
      path: generatePath(ROUTES.CUSTOMERS_CORPORATE_CARDS, {
        customerId: customerId as string,
      }),
      hidden: !cardsEnabled,
    },
    {
      name: 'AML & KYC',
      path: generatePath(ROUTES.CUSTOMERS_CORPORATE_AML, {
        customerId: customerId as string,
      }),
    },
    {
      name: 'Tariff',
      path: generatePath(ROUTES.CUSTOMERS_CORPORATE_TARIFFS, {
        customerId: customerId as string,
      }),
    },
    {
      name: 'Documents',
      path: generatePath(ROUTES.CUSTOMERS_CORPORATE_DOCUMENTS, {
        customerId: customerId as string,
      }),
      hidden: !activePermissions?.includes(
        PermissionEnum.CORPORATE_CUSTOMERS_DOCUMENTS_LIST,
      ),
    },
    {
      name: 'Statistics',
      path: generatePath(ROUTES.CUSTOMERS_CORPORATE_STATISTICS, {
        customerId: customerId as string,
      }),
    },
    {
      name: 'Beneficiaries',
      path: generatePath(ROUTES.CUSTOMERS_CORPORATE_BENEFICIARIES, {
        customerId: customerId as string,
      }),
      hidden: !activePermissions?.includes(PermissionEnum.BENEFICIARIES_LIST),
    },
  ];

  useEffect(() => {
    if (
      tabsWrapperRef &&
      tabsWrapperRef.current &&
      tabsRef &&
      tabsRef.current &&
      baseInformation &&
      !isLoading
    ) {
      if (
        tabsRef?.current?.scrollWidth > tabsWrapperRef?.current?.offsetWidth
      ) {
        setHiddenRoutes(defaultTabs.slice(-4));
        setTabs(defaultTabs.slice(0, -4));
        setWithOverflow(false);
      } else {
        setHiddenRoutes([]);
        setTabs(defaultTabs);
        setWithOverflow(true);
      }
    }
  }, [baseInformation, isLoading, tabsWrapperRef, tabsRef]);

  return { tabs, hiddenRoutes, withOverflow };
};

export default useGetOrganizationTabs;
